import { graphql } from 'app/generated/gql'
import { makeAutoObservable, runInAction } from 'mobx'
import { ClientType } from 'app/generated/graphql'
import type { Client } from '@urql/core'

import { legacyRoleRank } from 'app/types/roles'
import type { User as AuthUser } from 'app/generated/graphql'

const ME_QUERY = graphql(`
  query AuthStoreMe {
    me {
      id
      name
      email
      phone
      type
      orgId
      workspaceId
      position
      jobTitle
      timezone
      roleId
      permissions
      isOrgRole
      allowedLocationIds
      assignedWorkspaceIds
      workspaces {
        id
        roleId
        locationIds
      }
      products
    }
  }
`)

const WORKSPACE_QUERY = graphql(`
  query AuthWorkspace($id: ID!) {
    workspace(id: $id) {
      id
      name
    }
  }
`)

const LOGIN_MUTATION = graphql(`
  mutation Login($email: String!, $password: String!, $clientType: ClientType!) {
    login(email: $email, password: $password, clientType: $clientType) {
      accessToken
      pendingToken
      channel
    }
  }
`)

const VERIFY_OTP_MUTATION = graphql(`
  mutation VerifyOtp($pendingToken: String!, $code: String!, $clientType: ClientType!) {
    verifyOtp(pendingToken: $pendingToken, code: $code, clientType: $clientType) {
      accessToken
    }
  }
`)

const LOGIN_WITH_GOOGLE_CODE_MUTATION = graphql(`
  mutation LoginWithGoogleCode($code: String!, $redirectUri: String!, $clientType: ClientType!) {
    loginWithGoogleCode(code: $code, redirectUri: $redirectUri, clientType: $clientType) {
      accessToken
      pendingToken
      channel
    }
  }
`)

const LOGOUT_MUTATION = graphql(`
  mutation Logout {
    logout
  }
`)

const SWITCH_WORKSPACE_MUTATION = graphql(`
  mutation SwitchWorkspace($targetWorkspaceId: String) {
    switchWorkspace(targetWorkspaceId: $targetWorkspaceId) {
      accessToken
    }
  }
`)

type LoginResult = {
  accessToken?: string
  pendingToken?: string
  channel?: string
}

type LoginResponse = {
  accessToken: string | null
  pendingToken: string | null
  channel: string | null
}

function normalizeLoginResult(result: LoginResponse | undefined): LoginResult | undefined {
  if (!result) return undefined
  return {
    accessToken: result.accessToken ?? undefined,
    pendingToken: result.pendingToken ?? undefined,
    channel: result.channel ?? undefined,
  }
}

function normalizeCurrentUser(user: AuthUser | undefined): AuthUser | undefined {
  if (!user) return undefined
  if (typeof user.role === 'number') return user
  return {
    ...user,
    role: user.type === 'super' ? legacyRoleRank.orgAdmin : legacyRoleRank.workspaceUser,
  }
}

export class AuthStore {
  user: AuthUser | undefined = undefined
  workspace: { id: string; name?: string } | undefined = undefined
  permissions: string[] | undefined = undefined
  isOrgRole = false
  // `undefined` until `fetchMe` resolves (the not-loaded sentinel the nav gate relies on).
  // After load it is the workspace product union from me.products (an empty array means
  // "loaded, no products" — distinct from not-loaded). See hasNavProduct (TRK-5965).
  products: string[] | undefined = undefined
  loading = true
  error: string | undefined = undefined

  private client: Client

  constructor(client: Client) {
    this.client = client
    makeAutoObservable(this, { client: false } as never, { autoBind: true })
  }

  get isAuthenticated(): boolean {
    return !!this.user
  }

  get isSuper(): boolean {
    return this.user?.type === 'super'
  }

  get orgId(): string | undefined {
    return this.user?.orgId
  }

  get workspaceId(): string | undefined {
    return this.user?.workspaceId
  }

  // `background` re-checks the session without raising `loading`. AppLayout swaps the whole app
  // for a spinner while loading, which unmounts the page and throws away unsaved work.
  async fetchMe(options?: { requestPolicy?: 'cache-first' | 'network-only'; background?: boolean }) {
    const { background, ...context } = options ?? {}
    if (!background) this.loading = true
    this.error = undefined
    const result = await this.client.query(ME_QUERY, {}, context).toPromise()
    if (result.error) {
      runInAction(() => {
        this.loading = false
        this.error = result.error!.message
      })
      return result
    }
    runInAction(() => {
      this.user = normalizeCurrentUser(result.data?.me as AuthUser | undefined)
      this.products = result.data?.me?.products ?? []
      this.permissions = result.data?.me?.permissions ?? undefined
      this.isOrgRole = result.data?.me?.isOrgRole ?? false
    })
    // Fetch workspace details if user has a workspaceId
    if (this.user?.workspaceId) {
      const wsResult = await this.client.query(WORKSPACE_QUERY, { id: this.user.workspaceId }).toPromise()
      runInAction(() => {
        this.workspace = wsResult.data?.workspace || undefined
      })
    } else {
      runInAction(() => {
        this.workspace = undefined
      })
    }
    runInAction(() => {
      this.loading = false
    })
    return result
  }

  async login(
    email: string,
    password: string,
    clientType: ClientType = ClientType.Pwa,
  ): Promise<LoginResult | undefined> {
    this.loading = true
    this.error = undefined
    const result = await this.client.mutation(LOGIN_MUTATION, { email, password, clientType }).toPromise()
    runInAction(() => {
      this.loading = false
      if (result.error) {
        this.error = result.error.message
      }
    })
    return normalizeLoginResult(result.data?.login)
  }

  async verifyOtp(pendingToken: string, code: string, clientType: ClientType = ClientType.Pwa) {
    this.loading = true
    this.error = undefined
    const result = await this.client.mutation(VERIFY_OTP_MUTATION, { pendingToken, code, clientType }).toPromise()
    runInAction(() => {
      this.loading = false
      if (result.error) {
        this.error = result.error.message
      }
    })
    return result.data?.verifyOtp
  }

  async loginWithGoogleCode(
    code: string,
    redirectUri: string,
    clientType: ClientType = ClientType.Pwa,
  ): Promise<LoginResult | undefined> {
    this.loading = true
    this.error = undefined
    const result = await this.client
      .mutation(LOGIN_WITH_GOOGLE_CODE_MUTATION, { code, redirectUri, clientType })
      .toPromise()
    runInAction(() => {
      this.loading = false
      if (result.error) {
        this.error = result.error.message
      }
    })
    return normalizeLoginResult(result.data?.loginWithGoogleCode)
  }

  async switchWorkspace(workspaceId: string | null) {
    this.loading = true
    this.error = undefined
    const result = await this.client.mutation(SWITCH_WORKSPACE_MUTATION, { targetWorkspaceId: workspaceId }).toPromise()
    if (result.error) {
      runInAction(() => {
        this.loading = false
        this.error = result.error!.message
      })
    } else {
      await this.fetchMe({ requestPolicy: 'network-only' })
    }
    return result
  }

  async logout() {
    await this.client.mutation(LOGOUT_MUTATION, {}).toPromise()
    runInAction(() => {
      this.user = undefined
    })
  }

  reset() {
    this.user = undefined
    this.loading = false
    this.error = undefined
  }
}
